import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from 'components/Layout';
import { useParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button as MuiButton,
  TextField,
  Divider,
  IconButton,
  CircularProgress,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMst } from 'model/Root';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import type { DropResult, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import type { RootInstance } from 'model/Root';

type TocRow = {
  id: string;
  title: string;
  ebookPage: number | null;
  pdfPage: number | null;
  originalChapterId: string | null;
};

type TocRowItemProps = {
  row: TocRow;
  idx: number;
  t: (key: string, options?: Record<string, unknown>) => string;
  dragProvided: DraggableProvided;
  dragSnapshot: DraggableStateSnapshot;
  onChangeRow: (idx: number, field: 'title' | 'pdfPage' | 'ebookPage', value: string) => void;
  onApplyRowOffset: (idx: number) => void;
  onRemoveChapter: (idx: number) => void;
  setRowRef: (idx: number, el: HTMLDivElement | null) => void;
};

function TocRowItem({
  row,
  idx,
  t,
  dragProvided,
  dragSnapshot,
  onChangeRow,
  onApplyRowOffset,
  onRemoveChapter,
  setRowRef,
}: TocRowItemProps) {
  const [localTitle, setLocalTitle] = useState(row.title);
  const [localEbook, setLocalEbook] = useState<string>(row.ebookPage?.toString() ?? '');
  const [localPdf, setLocalPdf] = useState<string>(row.pdfPage?.toString() ?? '');

  useEffect(() => {
    setLocalTitle(row.title);
    setLocalEbook(row.ebookPage !== null && row.ebookPage !== undefined ? String(row.ebookPage) : '');
    setLocalPdf(row.pdfPage !== null && row.pdfPage !== undefined ? String(row.pdfPage) : '');
  }, [row.title, row.ebookPage, row.pdfPage]);

  return (
    <Grid
      container
      item
      xs={12}
      spacing={1}
      alignItems="center"
      ref={(el) => {
        dragProvided.innerRef(el);
        setRowRef(idx, el);
      }}
      {...dragProvided.draggableProps}
      style={{
        borderBottom: '1px solid #eee',
        background: dragSnapshot.isDragging ? '#f5f7ff' : undefined,
        ...dragProvided.draggableProps.style,
      }}
    >
      <Grid item xs={1}>
        <Tooltip title={t('csToc.dragToReorder')}>
          <IconButton
            size="small"
            aria-label={t('csToc.dragToReorder')}
            {...dragProvided.dragHandleProps}
            sx={{ cursor: 'grab' }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid item xs={5}>
        <TextField
          label={t('csToc.title')}
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          onBlur={() => onChangeRow(idx, 'title', localTitle)}
          fullWidth
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          label={t('csToc.ebook')}
          type="number"
          value={localEbook}
          onChange={(e) => setLocalEbook(e.target.value)}
          onBlur={() => onChangeRow(idx, 'ebookPage', localEbook)}
          fullWidth
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          label={t('csToc.pdf')}
          type="number"
          value={localPdf}
          onChange={(e) => setLocalPdf(e.target.value)}
          onBlur={() => onChangeRow(idx, 'pdfPage', localPdf)}
          fullWidth
        />
      </Grid>

      <Grid container item xs={12} justifyContent="flex-end" alignItems="center" spacing={1}>
        <Grid item>
          <MuiButton size="small" variant="outlined" onClick={() => onApplyRowOffset(idx)}>
            {t('csToc.setOffset')}
          </MuiButton>
        </Grid>
        <Grid item>
          <IconButton color="error" onClick={() => onRemoveChapter(idx)}>
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Grid>
  );
}
TocRowItem.displayName = 'TocRowItem';

function TocScreen() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { csAudiobookStore, errorStore } = useMst() as RootInstance;
  const details = id ? csAudiobookStore.detailsById.get(id) : null;
  const loading = id ? csAudiobookStore.loadingByKey.get(`details:${id}`) : false;
  const extracting = id ? csAudiobookStore.loadingByKey.get(`extract_toc:${id}`) : false;
  const navigate = useNavigate();

  const [rows, setRows] = useState<TocRow[]>([]);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [pageOffsetInput, setPageOffsetInput] = useState<string>('0');
  const [creating, setCreating] = useState(false);
  const [lastDraftAt, setLastDraftAt] = useState<number | null>(null);
  const draftKey = useMemo(() => (id ? `cs_toc_draft_${id}` : ''), [id]);
  const autosaveTimer = useRef<number | null>(null);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const rowIdRef = useRef(0);
  const latestRowsRef = useRef<TocRow[]>([]);
  const latestPageOffsetRef = useRef<number>(0);
  const [lastAddedIndex, setLastAddedIndex] = useState<number | null>(null);
  const [confirmDetectOpen, setConfirmDetectOpen] = useState(false);
  const [detectRemainingSec, setDetectRemainingSec] = useState<number>(0);
  const detectKey = useMemo(() => (id ? `cs_toc_detect_last_${id}` : ''), [id]);
  const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);
  const [tocRunIds, setTocRunIds] = useState<string[]>([]);
  const [tocSummary, setTocSummary] = useState<{ completed: number; total: number; message?: string } | null>(null);
  const tocPollRef = useRef<number | null>(null);
  const [chapterRunIds, setChapterRunIds] = useState<string[]>([]);
  const [chapterSummary, setChapterSummary] = useState<{ completed: number; total: number; message?: string } | null>(
    null
  );
  const chapterPollRef = useRef<number | null>(null);
  const chapterDetectRef = useRef<number | null>(null);

  useEffect(() => {
    if (id) {
      csAudiobookStore.getDetails(id);
    }
  }, [id, csAudiobookStore]);

  useEffect(() => {
    // Prefer locally saved draft whenever present; otherwise fall back to server details
    let draftLoaded = false;
    if (draftKey) {
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const parsed = JSON.parse(raw) as { rows: TocRow[]; pageOffset: number; savedAt: number };
          setRows(
            (parsed.rows ?? []).map((r) => ({
              ...r,
              id: r.id || `toc-row-${Date.now().toString(36)}-${rowIdRef.current++}`,
            }))
          );
          const po = parsed.pageOffset ?? 0;
          setPageOffset(po);
          setPageOffsetInput(String(po));
          setLastDraftAt(parsed.savedAt ?? null);
          draftLoaded = true;
        }
      } catch (err) {
        // ignore malformed draft
      }
    }
    if (!draftLoaded && details) {
      setRows(
        (details.toc ?? []).map((c) => ({
          id: `toc-row-${Date.now().toString(36)}-${rowIdRef.current++}`,
          title: c.title,
          ebookPage: c.ebookPage ?? null,
          // Display PDF page as 1-based for the user; backend is 0-based
          pdfPage: c.pdfPage !== null && c.pdfPage !== undefined ? c.pdfPage + 1 : null,
          originalChapterId: c.originalChapterId ?? null,
        }))
      );
      const po = details.pageOffset ?? 0;
      setPageOffset(po);
      setPageOffsetInput(String(po));
      setLastDraftAt(null);
    }
  }, [details, draftKey]);

  const applyOffset = () => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.ebookPage !== null && r.ebookPage !== undefined) {
          return { ...r, pdfPage: r.ebookPage + (pageOffset || 0) };
        }
        return r;
      })
    );
  };

  const onChangeRow = useCallback((idx: number, field: 'title' | 'pdfPage' | 'ebookPage', value: string) => {
    setRows((prev) => {
      if (idx < 0 || idx >= prev.length) return prev;
      const next = [...prev];
      const r = next[idx];
      if (!r) return prev;
      if (field === 'title') next[idx] = { ...r, title: value };
      if (field === 'pdfPage') next[idx] = { ...r, pdfPage: value === '' ? null : Number(value) };
      if (field === 'ebookPage') next[idx] = { ...r, ebookPage: value === '' ? null : Number(value) };
      return next;
    });
  }, []);

  const onAddChapter = useCallback(() => {
    setRows((prev) => {
      const next = [
        ...prev,
        {
          id: `toc-row-${Date.now().toString(36)}-${rowIdRef.current++}`,
          title: '',
          ebookPage: null,
          pdfPage: null,
          originalChapterId: null,
        },
      ];
      setLastAddedIndex(next.length - 1);
      return next;
    });
  }, []);

  useEffect(() => {
    if (lastAddedIndex !== null) {
      const el = rowRefs.current[lastAddedIndex];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setLastAddedIndex(null);
    }
  }, [lastAddedIndex]);

  // Track cooldown (3 minutes) between auto-detect triggers
  useEffect(() => {
    if (!confirmDetectOpen) return;
    const update = () => {
      try {
        const lastRaw = detectKey ? localStorage.getItem(detectKey) : null;
        const last = lastRaw ? Number(lastRaw) : 0;
        const now = Date.now();
        const remaining = Math.max(0, Math.ceil(180 - (now - last) / 1000));
        setDetectRemainingSec(remaining);
      } catch (_) {
        setDetectRemainingSec(0);
      }
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [confirmDetectOpen, detectKey]);

  // Poll TOC extraction runs
  useEffect(() => {
    if (!id) return;
    if (tocRunIds.length === 0) {
      if (tocPollRef.current) {
        window.clearInterval(tocPollRef.current);
        tocPollRef.current = null;
      }
      setTocSummary(null);
      return;
    }
    if (tocPollRef.current) return;
    tocPollRef.current = window.setInterval(
      async () => {
        try {
          type TocRun = {
            id?: unknown;
            status?: unknown;
            totalChapters?: unknown;
            completedChapters?: unknown;
            failedChapters?: unknown;
            lastMessage?: unknown;
          };
          const api = csAudiobookStore as unknown as {
            listRuns: (id: string, type?: 'toc_extraction') => Promise<unknown>;
          };
          const payload = (await api.listRuns(id, 'toc_extraction')) as unknown;
          const itemsUnknown = Array.isArray(payload)
            ? (payload as unknown[])
            : (payload as { items?: unknown[] })?.items;
          const runsArr: TocRun[] = Array.isArray(itemsUnknown) ? (itemsUnknown as TocRun[]) : [];
          const relevant = runsArr.filter((r) => tocRunIds.includes(String((r as { id?: unknown }).id ?? '')));
          if (relevant.length === 0) {
            setTocRunIds([]);
            return;
          }
          const total = relevant.reduce((acc: number, r) => {
            const n = Number((r as { totalChapters?: unknown }).totalChapters ?? 0);
            return acc + (Number.isFinite(n) ? n : 0);
          }, 0);
          const completed = relevant.reduce((acc: number, r) => {
            const c = Number((r as { completedChapters?: unknown }).completedChapters ?? 0);
            const f = Number((r as { failedChapters?: unknown }).failedChapters ?? 0);
            return acc + (Number.isFinite(c) ? c : 0) + (Number.isFinite(f) ? f : 0);
          }, 0);
          const running = relevant.find((r) => String((r as { status?: unknown }).status ?? '') === 'running');
          const message =
            running && typeof (running as { lastMessage?: unknown }).lastMessage === 'string'
              ? String((running as { lastMessage?: unknown }).lastMessage)
              : undefined;
          setTocSummary({ completed, total, message });
          const allDone = relevant.every((r) => String((r as { status?: unknown }).status ?? '') !== 'running');
          if (allDone) {
            setTocRunIds([]);
            await csAudiobookStore.getDetails(id);
          }
        } catch (_) {
          // ignore poll errors
        }
      },
      4500 + Math.floor(Math.random() * 1000)
    );
    return () => {
      if (tocPollRef.current) {
        window.clearInterval(tocPollRef.current);
        tocPollRef.current = null;
      }
    };
  }, [tocRunIds, id, csAudiobookStore]);

  // Poll chapter creation runs
  useEffect(() => {
    if (!id) return;
    if (chapterRunIds.length === 0) {
      if (chapterPollRef.current) {
        window.clearInterval(chapterPollRef.current);
        chapterPollRef.current = null;
      }
      setChapterSummary(null);
      return;
    }
    if (chapterPollRef.current) return;
    chapterPollRef.current = window.setInterval(
      async () => {
        try {
          type ChRun = {
            id?: unknown;
            status?: unknown;
            totalChapters?: unknown;
            completedChapters?: unknown;
            failedChapters?: unknown;
            lastMessage?: unknown;
          };
          const api = csAudiobookStore as unknown as {
            listRuns: (id: string, type?: 'chapter_creation') => Promise<unknown>;
          };
          const payload = (await api.listRuns(id, 'chapter_creation')) as unknown;
          const itemsUnknown = Array.isArray(payload)
            ? (payload as unknown[])
            : (payload as { items?: unknown[] })?.items;
          const runsArr: ChRun[] = Array.isArray(itemsUnknown) ? (itemsUnknown as ChRun[]) : [];
          const relevant = runsArr.filter((r) => chapterRunIds.includes(String((r as { id?: unknown }).id ?? '')));
          if (relevant.length === 0) {
            setChapterRunIds([]);
            return;
          }
          const total = relevant.reduce((acc: number, r) => {
            const n = Number((r as { totalChapters?: unknown }).totalChapters ?? 0);
            return acc + (Number.isFinite(n) ? n : 0);
          }, 0);
          const completed = relevant.reduce((acc: number, r) => {
            const c = Number((r as { completedChapters?: unknown }).completedChapters ?? 0);
            const f = Number((r as { failedChapters?: unknown }).failedChapters ?? 0);
            return acc + (Number.isFinite(c) ? c : 0) + (Number.isFinite(f) ? f : 0);
          }, 0);
          const running = relevant.find((r) => String((r as { status?: unknown }).status ?? '') === 'running');
          const message =
            running && typeof (running as { lastMessage?: unknown }).lastMessage === 'string'
              ? String((running as { lastMessage?: unknown }).lastMessage)
              : undefined;
          setChapterSummary({ completed, total, message });
          const allDone = relevant.every((r) => String((r as { status?: unknown }).status ?? '') !== 'running');
          if (allDone) {
            setChapterRunIds([]);
            await csAudiobookStore.getDetails(id);
            // Auto-navigate to Chapters screen after chapter creation completes
            try {
              navigate(`/cs-audiobooks/${id}/chapters?autoProcess=1`);
            } catch (_) {
              // ignore navigation errors
            }
          }
        } catch (_) {
          // ignore
        }
      },
      4500 + Math.floor(Math.random() * 1000)
    );
    return () => {
      if (chapterPollRef.current) {
        window.clearInterval(chapterPollRef.current);
        chapterPollRef.current = null;
      }
    };
  }, [chapterRunIds, id, csAudiobookStore, navigate]);
  const onRemoveChapter = useCallback((idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const onApplyRowOffset = useCallback(
    (idx: number) => {
      setRows((prev) => {
        const next = [...prev];
        const r = next[idx];
        if (r && r.ebookPage !== null && r.ebookPage !== undefined) {
          next[idx] = { ...r, pdfPage: r.ebookPage + (pageOffset || 0) };
        }
        return next;
      });
    },
    [pageOffset]
  );

  useEffect(() => {
    latestRowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    latestPageOffsetRef.current = pageOffset;
  }, [pageOffset]);

  const doCreateChapters = async () => {
    if (!id) return;
    setCreating(true);
    const chapters = rows
      .filter((r) => r.pdfPage && r.pdfPage > 0)
      .sort((a, b) => (a.pdfPage ?? 0) - (b.pdfPage ?? 0))
      .map((r) => ({
        title: r.title,
        // Convert user-facing 1-based PDF page to backend 0-based
        pdf_page: r.pdfPage !== null && r.pdfPage !== undefined ? Math.max(0, (r.pdfPage as number) - 1) : undefined,
        ebook_page: r.ebookPage ?? undefined,
        original_chapter_id: r.originalChapterId ?? undefined,
      }));

    // Guard: backend requires a non-empty chapters array with pdf_page values
    if (!Array.isArray(chapters) || chapters.length === 0) {
      setCreating(false);
      try {
        errorStore?.setError?.('Lisää vähintään yksi luku ja aseta PDF‑sivu');
      } catch (_) {
        // fall back silently
      }
      return;
    }

    const data = (await csAudiobookStore.saveToc(id, { page_offset: Number(pageOffset) || 0, chapters })) as
      | { runId?: string; run_id?: string; id?: string }
      | undefined;
    const returnedRunId = (data?.runId as string) || (data?.run_id as string) || (data?.id as string) || '';
    if (returnedRunId) {
      setChapterRunIds((prev) => Array.from(new Set([...prev, String(returnedRunId)])));
    } else {
      // Fallback: try to find a recent chapter_creation run; poll briefly to catch eventual consistency
      const api = csAudiobookStore as unknown as {
        listRuns?: (
          aid: string,
          type?: 'chapter_creation'
        ) => Promise<
          | { items?: Array<{ id?: string; status?: string; startedAt?: string }> }
          | Array<{ id?: string; status?: string; startedAt?: string }>
          | null
        >;
      };
      const detectOnce = async (): Promise<boolean> => {
        try {
          if (!api?.listRuns) return false;
          const payload = await api.listRuns(id, 'chapter_creation');
          const items = Array.isArray(payload)
            ? (payload as Array<{ id?: string; status?: string; startedAt?: string }>)
            : ((payload?.items as Array<{ id?: string; status?: string; startedAt?: string }>) ?? []);
          let chosen = items.find((r) => String(r.status || '').toLowerCase() === 'running');
          if (!chosen && items.length > 0) {
            chosen = items.slice().sort((a, b) => Date.parse(b.startedAt || '') - Date.parse(a.startedAt || ''))[0];
          }
          const fallbackRunId = chosen?.id ? String(chosen.id) : null;
          if (fallbackRunId) {
            setChapterRunIds((prev) => Array.from(new Set([...prev, fallbackRunId])));
            return true;
          }
          return false;
        } catch {
          return false;
        }
      };
      // Clear any previous detect timer
      if (chapterDetectRef.current) {
        window.clearInterval(chapterDetectRef.current);
        chapterDetectRef.current = null;
      }
      let attempts = 0;
      const done = await detectOnce();
      if (!done) {
        chapterDetectRef.current = window.setInterval(async () => {
          attempts += 1;
          const ok = await detectOnce();
          if (ok || attempts >= 5) {
            if (chapterDetectRef.current) {
              window.clearInterval(chapterDetectRef.current);
              chapterDetectRef.current = null;
            }
            // Release spinner if still waiting
            setCreating(false);
          }
        }, 1500);
      }
    }
    // Indicate processing; keep user on TOC to show progress banner
    try {
      const setStatusMaybe = csAudiobookStore as unknown as {
        setStatus?: (aid: string, status: string) => Promise<unknown>;
      };
      if (setStatusMaybe.setStatus) {
        await setStatusMaybe.setStatus(id, 'processing');
      }
    } catch (_) {
      // ignore if backend doesn't accept this status; navigation still proceeds
    }
    await csAudiobookStore.getDetails(id);
    // Clear draft upon successful creation
    if (draftKey) localStorage.removeItem(draftKey);
    // If we already have a runId, we can release the spinner now;
    // otherwise the fallback detect loop will release it or timeout.
    if (returnedRunId) {
      setCreating(false);
    }
  };

  const onSaveCreateChapters = () => {
    setConfirmCreateOpen(true);
  };

  const onSaveDraft = () => {
    if (!id || !draftKey) return;
    const payload = {
      rows,
      pageOffset,
      savedAt: Date.now(),
    };
    localStorage.setItem(draftKey, JSON.stringify(payload));
    setLastDraftAt(payload.savedAt);
    // Show explicit feedback only for manual saves
    try {
      errorStore?.setSuccess?.('save_success');
    } catch (_) {
      // ignore if store shape differs
    }
  };

  // Drag & drop helpers
  const reorder = (list: TocRow[], startIndex: number, endIndex: number): TocRow[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    setRows((prev) => reorder(prev, result.source.index, result.destination.index));
  };

  // Autosave every 15s
  useEffect(() => {
    if (!draftKey) return;
    if (autosaveTimer.current) window.clearInterval(autosaveTimer.current);
    autosaveTimer.current = window.setInterval(() => {
      try {
        const payload = {
          rows: latestRowsRef.current,
          pageOffset: latestPageOffsetRef.current,
          savedAt: Date.now(),
        };
        localStorage.setItem(draftKey, JSON.stringify(payload));
        setLastDraftAt(payload.savedAt);
      } catch (err) {
        // ignore autosave failures (e.g., storage full)
      }
    }, 15000) as unknown as number;
    return () => {
      if (autosaveTimer.current) window.clearInterval(autosaveTimer.current);
    };
  }, [draftKey]);

  type AbListItem = { id?: string | number; title?: string; name?: string };
  const fallbackName = (() => {
    if (!id) return '';
    try {
      const arr = (csAudiobookStore.itemsArray as unknown[]) || [];
      const found = arr.find((x) => {
        const y = x as AbListItem;
        return String(y?.id ?? '') === String(id);
      }) as AbListItem | undefined;
      return (found?.title as string) || (found?.name as string) || '';
    } catch (_) {
      return '';
    }
  })();

  const titleText = details?.title ? `${details.title}` : fallbackName || `TOC`;
  const statusText = useMemo(() => {
    const raw = String(details?.status ?? '').toLowerCase();
    if (!raw) return '';
    const mapped = t(`csToc.status.${raw}`);
    return mapped || raw;
  }, [details?.status, t]);

  return (
    <>
      <Layout title={titleText}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {loading ? (
              <Typography variant="body1">Ladataan…</Typography>
            ) : (
              <>
                <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                  <Grid item>
                    <MuiButton onClick={() => navigate('/cs-audiobooks/ocr')}>{t('csToc.back')}</MuiButton>
                    <Typography variant="body2" color="GrayText">
                      {details?.author ?? ''} • {details?.language ?? ''} • {statusText}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <IconButton onClick={() => id && csAudiobookStore.getDetails(id)} size="small" sx={{ mr: 1 }}>
                      <RefreshIcon />
                    </IconButton>
                    <MuiButton
                      sx={{ mr: 1 }}
                      variant="outlined"
                      onClick={onSaveDraft}
                      disabled={!!creating || chapterRunIds.length > 0}
                    >
                      {t('csToc.saveDraft')}
                    </MuiButton>
                    <MuiButton
                      variant="contained"
                      onClick={onSaveCreateChapters}
                      disabled={!!creating || chapterRunIds.length > 0}
                    >
                      {creating ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <span>{t('csToc.saveAndCreate')}</span>
                        </Box>
                      ) : (
                        t('csToc.saveAndCreate')
                      )}
                    </MuiButton>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <Typography variant="body1">
                      {t('csToc.pageOffset')}: {pageOffset || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label={t('csToc.pageOffset')}
                      type="text"
                      value={pageOffsetInput}
                      onChange={(e) => {
                        const v = e.target.value;
                        // Allow "", "-", or integer with optional leading minus
                        if (/^$|^-$|^-?\d+$/.test(v)) {
                          setPageOffsetInput(v);
                          if (v === '' || v === '-') return;
                          setPageOffset(Number(v));
                        }
                      }}
                      inputProps={{ inputMode: 'numeric', pattern: '-?\\d*' }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item>
                    <MuiButton variant="outlined" onClick={applyOffset}>
                      {t('csToc.applyOffsetToAll')}
                    </MuiButton>
                  </Grid>
                  <Grid item>
                    <MuiButton
                      variant="outlined"
                      disabled={!!extracting}
                      onClick={async () => {
                        setConfirmDetectOpen(true);
                      }}
                    >
                      {extracting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <span>{t('csToc.detectingToc')}</span>
                        </Box>
                      ) : (
                        t('csToc.autoDetectToc')
                      )}
                    </MuiButton>
                  </Grid>
                  <Grid item>
                    <MuiButton variant="outlined" onClick={onAddChapter}>
                      {t('csToc.addChapter')}
                    </MuiButton>
                  </Grid>

                  {lastDraftAt && (
                    <Grid item>
                      <Typography variant="caption" color="GrayText">
                        {t('csToc.draftSavedAt')} {new Date(lastDraftAt).toLocaleTimeString()}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Grid container spacing={1}>
                      {/* no header row; each block shows its own labels */}

                      {extracting && (
                        <Grid container item xs={12} alignItems="center" spacing={1}>
                          <Grid item>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={16} />
                              <Typography variant="body2">{t('csToc.detectingToc')}</Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      )}

                      <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="toc-rows-droppable">
                          {(provided, snapshot) => (
                            <Grid
                              container
                              spacing={1}
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              style={{
                                background: snapshot.isDraggingOver ? '#ececff' : undefined,
                                borderRadius: 5,
                              }}
                            >
                              {rows.map((row, idx) => (
                                <Draggable
                                  key={row.id || `toc-row-${idx}`}
                                  draggableId={row.id || `toc-row-${idx}`}
                                  index={idx}
                                >
                                  {(dragProvided, dragSnapshot) => (
                                    <TocRowItem
                                      row={row}
                                      idx={idx}
                                      t={t}
                                      dragProvided={dragProvided}
                                      dragSnapshot={dragSnapshot}
                                      onChangeRow={onChangeRow}
                                      onApplyRowOffset={onApplyRowOffset}
                                      onRemoveChapter={onRemoveChapter}
                                      setRowRef={(i, el) => {
                                        dragProvided.innerRef(el);
                                        rowRefs.current[i] = el;
                                      }}
                                    />
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </Grid>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <MuiButton
                      sx={{ mr: 1 }}
                      variant="outlined"
                      onClick={onSaveDraft}
                      disabled={!!creating || chapterRunIds.length > 0}
                    >
                      {t('csToc.saveDraft')}
                    </MuiButton>
                    <MuiButton
                      variant="contained"
                      onClick={onSaveCreateChapters}
                      disabled={!!creating || chapterRunIds.length > 0}
                    >
                      {creating ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <span>{t('csToc.saveAndCreate')}</span>
                        </Box>
                      ) : (
                        t('csToc.saveAndCreate')
                      )}
                    </MuiButton>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Layout>
      {/* Confirm Auto-detect TOC */}
      <Dialog open={confirmDetectOpen} onClose={() => setConfirmDetectOpen(false)}>
        <DialogTitle>{t('csToc.confirmDetectTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('csToc.confirmDetectBody')}
          </Typography>
          {detectRemainingSec > 0 && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
              {t('csToc.waitBeforeRetry', { seconds: detectRemainingSec })}
            </Typography>
          )}
          {/* No additional confirmation checkbox needed */}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmDetectOpen(false)}>{t('cancel')}</MuiButton>
          <MuiButton
            variant="contained"
            onClick={async () => {
              if (!id) return;
              try {
                // mark last detect time to enforce cooldown
                if (detectKey) localStorage.setItem(detectKey, String(Date.now()));
                const res = (await csAudiobookStore.autoDetectToc(id)) as { runId?: string } | undefined;
                if (res?.runId) {
                  setTocRunIds((prev) => Array.from(new Set([...prev, String(res.runId)])));
                }
              } finally {
                setConfirmDetectOpen(false);
              }
            }}
            disabled={detectRemainingSec > 0 || !!extracting}
          >
            {t('csToc.confirm')}
          </MuiButton>
        </DialogActions>
      </Dialog>
      {/* Confirm Create Chapters */}
      <Dialog open={confirmCreateOpen} onClose={() => setConfirmCreateOpen(false)}>
        <DialogTitle>{t('csToc.confirmCreateTitle', 'Luo luvut?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t(
              'csToc.confirmCreateBody',
              'Tämä luo luvut nykyisestä sisällysluettelosta ja korvaa aiemmat. Jatketaanko?'
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmCreateOpen(false)}>{t('cancel')}</MuiButton>
          <MuiButton
            variant="contained"
            onClick={async () => {
              setConfirmCreateOpen(false);
              await doCreateChapters();
            }}
            disabled={!!creating}
          >
            {t('csToc.confirm')}
          </MuiButton>
        </DialogActions>
      </Dialog>
      {/* Progress banners */}
      {(tocSummary || chapterSummary) && (
        <Box sx={{ position: 'fixed', left: 16, right: 16, bottom: 16, zIndex: 1300 }}>
          {tocSummary && (
            <Box
              sx={{ mb: 1, p: 1, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, bgcolor: 'background.paper' }}
            >
              <Typography variant="body2">
                TOC:tä tunnistetaan… {tocSummary.completed}/{tocSummary.total}
              </Typography>
              {tocSummary.message && (
                <Typography variant="caption" color="GrayText">
                  {tocSummary.message}
                </Typography>
              )}
              <Box sx={{ mt: 0.5, height: 6, bgcolor: 'action.hover', borderRadius: 999, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width:
                      tocSummary.total && tocSummary.total > 0
                        ? `${Math.min(100, Math.round((tocSummary.completed / tocSummary.total) * 100))}%`
                        : '0%',
                    bgcolor: 'primary.main',
                  }}
                />
              </Box>
            </Box>
          )}
          {chapterSummary && (
            <Box sx={{ p: 1, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, bgcolor: 'background.paper' }}>
              <Typography variant="body2">
                Lukuja luodaan… {chapterSummary.completed}/{chapterSummary.total}
              </Typography>
              {chapterSummary.message && (
                <Typography variant="caption" color="GrayText">
                  {chapterSummary.message}
                </Typography>
              )}
              <Box sx={{ mt: 0.5, height: 6, bgcolor: 'action.hover', borderRadius: 999, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width:
                      chapterSummary.total && chapterSummary.total > 0
                        ? `${Math.min(100, Math.round((chapterSummary.completed / chapterSummary.total) * 100))}%`
                        : '0%',
                    bgcolor: 'primary.main',
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
export default observer(TocScreen);
